import { Routes, Route, Navigate } from "react-router-dom";
import { SidebarProvider, useSidebar } from "@/components/ui/sidebar";
import { Button } from "@/components/ui/button";
import { Menu } from "lucide-react";
import { useAuth } from "@/contexts/auth.context";
import { useAccount } from "@/contexts/account.context";
import { useTheme } from "@/hooks/use-theme";
import AppSidebar from "./sidebar/AppSidebar";
import { BottomNavigationMobile, DrawerMobile } from "./mobile";
import { MobileHeader } from "./mobile/MobileHeader";
import UnifiedOverview from "./dashboard/UnifiedOverview";
import AIInsightsPage from "./pages/AIInsightsPage";
import GoogleAnalyticsPage from "./pages/GoogleAnalyticsPage";
import MetaAdsPage from "./pages/MetaAdsPage";
import GoogleAdsPage from "./pages/GoogleAdsPage";
import SearchConsolePage from "./pages/SearchConsolePage";

import HealthDashboardPage from "./pages/HealthDashboardPage";
import AlertsPage from "./pages/AlertsPage";
import ProductsPage from "./pages/ProductsPage";
import IntegrationsPage from "./pages/IntegrationsPage";
import AccountSelectionPage from "./pages/AccountSelectionPage";
import AccountsPage from "./pages/AccountsPage";
import AccountPage from "./pages/AccountPage";
import MediaPerformancePage from "./pages/MediaPerformancePage";
import DiagnosticoPage from "./pages/DiagnosticoPage";
import MediaChatPage from "./pages/MediaChatPage";
import ConversionRatePage from "./pages/ConversionRatePage";
import AcquisitionPage from "./pages/AcquisitionPage";
import UTMPage from "./pages/UTMPage";
import PagesPage from "./pages/PagesPage";
import ReceitaPage from "./pages/ReceitaPage";
import RegiaoPage from "./pages/RegiaoPage";
import SEOPage from "./pages/SEOPage";
import InstagramPage from "./pages/InstagramPage";
import HistoricoPage from "./pages/HistoricoPage";
import ADSPage from "./pages/ADSPage";
import BenchmarkPage from "./pages/BenchmarkPage";
import AnaliseDiariaPage from "./pages/AnaliseDiariaPage";
import AnalisePedidosPage from "./pages/AnalisePedidosPage";
import AnaliseMensalPage from "./pages/AnaliseMensalPage";
import DetalhamentoEtapa1Page from "./pages/DetalhamentoEtapa1Page";
import AnaliseTicketMedioPage from "./pages/AnaliseTicketMedioPage";
import MelhoresResultadosMetaPage from "./pages/MelhoresResultadosMetaPage";
import MyAccountPage from "./pages/MyAccountPage";
import TeamsPage from "./pages/TeamsPage";
import TeamDetailPage from "./pages/TeamDetailPage";
import TeamScopeBanner from "./common/TeamScopeBanner";
import IntegrationGuard from "./auth/IntegrationGuard";
import ExigeLojaAtiva from "./auth/ExigeLojaAtiva";
import ExigePlanoAtivo from "./auth/ExigePlanoAtivo";
import FeatureGate from "./auth/FeatureGate";
import { SLUGS } from "@/lib/feature-gates";
import AccountSelectionModal from "./accounts/AccountSelectionModal";
import FeedbackButton from "./feedback/FeedbackButton";
import { authDbg } from "@/lib/auth-debug-logger";
import React, { useEffect, useState } from "react";
import {
  getStorageUsageApiV1StorageUsageGet,
  readRetentionStatusApiV1SubscriptionsRetentionStatusGet,
} from "@/backend-sdk/client/sdk.gen";
import type {
  RetentionStatusResponse,
  StorageUsageResponse,
} from "@/backend-sdk/client/types.gen";
import { AlertTriangle, Database } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { ehAssinante } from "@/lib/account-access-context";

const DashboardMain = () => {
  const { state, toggleSidebar, isMobile } = useSidebar();
  const { user, logout } = useAuth();
  const {
    isAgencyUser,
    canSwitchAccounts,
    accounts,
    selectedAccount,
    loading,
    hasChosenMainAccount,
    isTeamScoped,
    isReadOnly,
    acessoResolvido,
  } = useAccount();
  const { isDark, toggleTheme } = useTheme();
  const [drawerOpen, setDrawerOpen] = React.useState(false);
  const [accountModalOpen, setAccountModalOpen] = React.useState(false);
  const accountModalDismissedRef = React.useRef(false);
  const [retentionStatus, setRetentionStatus] =
    useState<RetentionStatusResponse | null>(null);
  const [storageUsage, setStorageUsage] = useState<StorageUsageResponse | null>(
    null,
  );
  const navigate = useNavigate();

  const showAccountModal =
    canSwitchAccounts &&
    accounts.length > 0 &&
    !selectedAccount &&
    !loading &&
    !hasChosenMainAccount;

  authDbg(
    `[AUTH_DBG] Dashboard: showAccountModal=${showAccountModal} canSwitchAccounts=${canSwitchAccounts} isAgencyUser=${isAgencyUser} accounts.length=${accounts.length} selectedAccount=${selectedAccount?.id || "null"} loading=${loading}`,
  );

  const handleAccountModalChange = (open: boolean) => {
    if (!open) {
      accountModalDismissedRef.current = true;
    }
    setAccountModalOpen(open);
  };

  useEffect(() => {
    if (showAccountModal && !accountModalDismissedRef.current) {
      authDbg("[AUTH_DBG] Dashboard: auto-opening account selection modal");
      setAccountModalOpen(true);
    }
  }, [showAccountModal]);

  useEffect(() => {
    readRetentionStatusApiV1SubscriptionsRetentionStatusGet({
      throwOnError: true,
    })
      .then(({ data }) => setRetentionStatus(data))
      .catch(() => {});
    getStorageUsageApiV1StorageUsageGet({
      throwOnError: true,
    })
      .then(({ data }) => setStorageUsage(data))
      .catch(() => {});
  }, []);

  return (
    <div className="min-h-screen flex w-full bg-background relative">
      {/* Header Mobile Fixo */}
      <div className="lg:hidden">
        <MobileHeader />
      </div>

      {/* Sidebar para desktop */}
      <div className="hidden lg:block">
        <AppSidebar
          onLogout={logout}
          darkMode={isDark}
          onToggleDarkMode={toggleTheme}
          user={user}
          onOpenAccountSelector={() => setAccountModalOpen(true)}
        />
      </div>

      <AccountSelectionModal
        open={accountModalOpen}
        onOpenChange={handleAccountModalChange}
      />

      {/* Botão flutuante para expandir menu quando colapsado (apenas desktop) */}
      {state === "collapsed" && !isMobile && (
        <Button
          variant="outline"
          size="icon"
          className="fixed top-4 left-4 z-50 shadow-lg bg-background border-border"
          onClick={toggleSidebar}
        >
          <Menu className="h-4 w-4" />
        </Button>
      )}

      {/* Conteúdo principal. O respiro de baixo é o da barra de navegação mobile mais o do
          botão de feedback (M-281), para que o fim de uma tabela não fique sob ele. */}
      <main className="flex-1 overflow-auto pb-32 lg:pb-20 pt-16 lg:pt-0 relative">
        {/* Membro de time: a faixa que diz o que este acesso é. */}
        <TeamScopeBanner />

        {/* Banner de retenção de dados */}
        {retentionStatus?.status === "in_retention" && (
          <div className="bg-red-50 border-b border-red-200 px-4 sm:px-6 py-3">
            <div className="flex items-center justify-between gap-4 max-w-7xl mx-auto">
              <div className="flex items-center gap-3 min-w-0">
                <AlertTriangle className="h-5 w-5 text-red-500 shrink-0" />
                <p className="text-sm text-red-800">
                  {retentionStatus.message}
                </p>
              </div>
              {/* <M-178> "Reativar Assinatura", e não "Ativar Plano": o banner passou a
                  alcançar quem **tem** plano. Depois de <D-079> a suspensão por falta de
                  pagamento também inicia o relógio de retenção, então o cliente que deve
                  uma fatura vê esta faixa — e o destino é o mesmo, Minha Conta. */}
              <Button
                size="sm"
                variant="destructive"
                className="shrink-0"
                onClick={() => navigate("/dashboard/my-account")}
              >
                Reativar Assinatura
              </Button>
            </div>
          </div>
        )}
        {retentionStatus?.status === "pending_deletion" && (
          <div className="bg-gray-100 border-b border-gray-200 px-4 sm:px-6 py-3">
            <div className="flex items-center gap-3 max-w-7xl mx-auto">
              <AlertTriangle className="h-5 w-5 text-gray-500 shrink-0" />
              <p className="text-sm text-gray-600">{retentionStatus.message}</p>
            </div>
          </div>
        )}

        {/* Banner de armazenamento */}
        {storageUsage?.is_exceeded && (
          <div className="bg-red-50 border-b border-red-200 px-4 sm:px-6 py-3">
            <div className="flex items-center justify-between gap-4 max-w-7xl mx-auto">
              <div className="flex items-center gap-3 min-w-0">
                <Database className="h-5 w-5 text-red-500 shrink-0" />
                <p className="text-sm text-red-800">
                  Limite de armazenamento excedido. Faça upgrade para continuar
                  sincronizando novos dados.
                </p>
              </div>
              <Button
                size="sm"
                variant="destructive"
                className="shrink-0"
                onClick={() => navigate("/dashboard/my-account")}
              >
                Fazer Upgrade
              </Button>
            </div>
          </div>
        )}
        {storageUsage?.is_near_limit && !storageUsage?.is_exceeded && (
          <div className="bg-amber-50 border-b border-amber-200 px-4 sm:px-6 py-3">
            <div className="flex items-center justify-between gap-4 max-w-7xl mx-auto">
              <div className="flex items-center gap-3 min-w-0">
                <Database className="h-5 w-5 text-amber-500 shrink-0" />
                <p className="text-sm text-amber-800">
                  Seu armazenamento está próximo do limite. Considere fazer
                  upgrade para garantir que suas integrações continuem
                  sincronizando.
                </p>
              </div>
              <Button
                size="sm"
                variant="default"
                className="shrink-0 bg-amber-600 hover:bg-amber-700"
                onClick={() => navigate("/dashboard/my-account")}
              >
                Ver Planos
              </Button>
            </div>
          </div>
        )}

        <Routes>
          {/* Minha Conta e Perfil abrem mesmo sem plano: é em Minha Conta que se assina ou se
              paga a fatura em atraso, e perfil, senha e 2FA não dependem de plano. Ver
              `ExigePlanoAtivo`. */}
          <Route path="/my-account" element={<MyAccountPage />} />
          {/*
            <M-249> Fora do `IntegrationGuard`, como `/my-account` sempre esteve. Perfil,
            senha, 2FA e sessões não são análise de dados: guardá-los atrás de "tem
            integração conectada?" mandava o lojista recém-cadastrado para
            `/dashboard/integrations` justamente quando ele ia trocar a própria senha.
          */}
          <Route path="/account" element={<AccountPage />} />

          {/* Daqui para baixo, o assinante sem plano vai para a escolha do plano. */}
          <Route element={<ExigePlanoAtivo />}>
            {/* Páginas permitidas para usuários sem integrações.
              <M-248> "Contas da Agência" não é escrita em loja nenhuma — é a agência gerindo
              as contas dela —, e `GET /api/v1/accounts` responde `403 Usuário não é um
              assinante` a quem navega por escopo cedido, seja ele `viewer` ou `editor`. */}
            <Route
              path="/account-selection"
              element={<AccountSelectionPage />}
            />
            <Route
              path="/accounts"
              element={
                // <M-279> O mesmo predicado do menu. Com o perfil ainda carregando, não
                // redireciona: `ehAssinante(null)` é "não sei", e não "não é".
                isTeamScoped || (user != null && !ehAssinante(user)) ? (
                  <Navigate to="/dashboard" replace />
                ) : (
                  <AccountsPage />
                )
              }
            />
            <Route path="/teams" element={<TeamsPage />} />
            <Route path="/teams/:teamId" element={<TeamDetailPage />} />

            {/* <M-263> Daqui para baixo, toda tela é de uma loja: sem loja ativa, nenhuma monta
              e nenhuma chamada sai com `customer_uuid` vazio. As de cima não dependem de
              loja e ficam de fora. Ver `ExigeLojaAtiva`. */}
            <Route
              element={
                <ExigeLojaAtiva
                  onEscolherLoja={() => setAccountModalOpen(true)}
                />
              }
            >
              {/* Conectar provedor é escrita **na loja**, e esconder o item do menu não basta:
              a URL é digitável, e cair numa tela cheia de ações que falham é pior do que não
              chegar nela. <M-248> Quem for `editor` na loja pode, e o desvio é sobre o
              nível. */}
              {/* O desvio espera a resposta. `isReadOnly` começa `true` para todo membro —
              a loja ativa ainda não foi escolhida —, e um `<Navigate>` renderizado nesse
              quadro já está confirmado quando a resposta chega: o `editor` que recarregasse
              a página aqui cairia no painel sem entender por quê. Achado de code review. */}
              <Route
                path="/integrations"
                element={
                  !acessoResolvido ? null : isReadOnly ? (
                    <Navigate to="/dashboard" replace />
                  ) : (
                    <IntegrationsPage />
                  )
                }
              />

              {/* Outras páginas protegidas por IntegrationGuard */}
              <Route
                path="/"
                element={
                  <IntegrationGuard>
                    <FeatureGate featureSlug={SLUGS["analytics:visao-geral"]}>
                      <UnifiedOverview />
                    </FeatureGate>
                  </IntegrationGuard>
                }
              />
              <Route
                path="/benchmark"
                element={
                  <IntegrationGuard>
                    <FeatureGate featureSlug={SLUGS["analytics:benchmark"]}>
                      <BenchmarkPage />
                    </FeatureGate>
                  </IntegrationGuard>
                }
              />
              <Route
                path="/ai-insights"
                element={
                  <IntegrationGuard>
                    <FeatureGate featureSlug={SLUGS["analytics:insights-ia"]}>
                      <AIInsightsPage />
                    </FeatureGate>
                  </IntegrationGuard>
                }
              />
              <Route
                path="/google-analytics"
                element={
                  <IntegrationGuard>
                    <FeatureGate
                      featureSlug={SLUGS["analytics:google-analytics-page"]}
                    >
                      <GoogleAnalyticsPage />
                    </FeatureGate>
                  </IntegrationGuard>
                }
              />
              <Route
                path="/meta-ads"
                element={
                  <IntegrationGuard>
                    <FeatureGate featureSlug={SLUGS["analytics:meta-ads-page"]}>
                      <MetaAdsPage />
                    </FeatureGate>
                  </IntegrationGuard>
                }
              />
              <Route
                path="/google-ads"
                element={
                  <IntegrationGuard>
                    <FeatureGate
                      featureSlug={SLUGS["analytics:google-ads-page"]}
                    >
                      <GoogleAdsPage />
                    </FeatureGate>
                  </IntegrationGuard>
                }
              />
              <Route
                path="/search-console"
                element={
                  <IntegrationGuard>
                    <FeatureGate
                      featureSlug={SLUGS["analytics:search-console-page"]}
                    >
                      <SearchConsolePage />
                    </FeatureGate>
                  </IntegrationGuard>
                }
              />

              <Route
                path="/health"
                element={
                  <IntegrationGuard>
                    <FeatureGate featureSlug={SLUGS["analytics:saude-do-site"]}>
                      <HealthDashboardPage />
                    </FeatureGate>
                  </IntegrationGuard>
                }
              />
              <Route
                path="/alerts"
                element={
                  <IntegrationGuard>
                    <FeatureGate featureSlug={SLUGS["automation:alertas"]}>
                      <AlertsPage />
                    </FeatureGate>
                  </IntegrationGuard>
                }
              />
              <Route
                path="/products"
                element={
                  <IntegrationGuard>
                    <FeatureGate featureSlug={SLUGS["analytics:produtos"]}>
                      <ProductsPage />
                    </FeatureGate>
                  </IntegrationGuard>
                }
              />
              <Route
                path="/media-performance"
                element={
                  <IntegrationGuard>
                    <FeatureGate
                      featureSlug={SLUGS["analytics:media-performance"]}
                    >
                      <MediaPerformancePage />
                    </FeatureGate>
                  </IntegrationGuard>
                }
              />
              <Route
                path="/media-chat"
                element={
                  <FeatureGate featureSlug={SLUGS["analytics:arquiteto"]}>
                    <MediaChatPage />
                  </FeatureGate>
                }
              />
              <Route
                path="/diagnostico"
                element={
                  <FeatureGate featureSlug={SLUGS["analytics:insights-ia"]}>
                    <DiagnosticoPage />
                  </FeatureGate>
                }
              />
              <Route
                path="/conversion-rate"
                element={
                  <IntegrationGuard>
                    <FeatureGate
                      featureSlug={SLUGS["analytics:conversion-rate"]}
                    >
                      <ConversionRatePage />
                    </FeatureGate>
                  </IntegrationGuard>
                }
              />
              <Route
                path="/acquisition"
                element={
                  <IntegrationGuard>
                    <FeatureGate featureSlug={SLUGS["analytics:aquisicao"]}>
                      <AcquisitionPage />
                    </FeatureGate>
                  </IntegrationGuard>
                }
              />
              {/* <auditoria 28> ANÁLISE DE PEDIDOS. A feature é a mesma que
              `GET /api/v1/acquisition/orders` exige — com outra, um plano poderia abrir a
              tela e receber 403 da rota. */}
              <Route
                path="/analise-pedidos"
                element={
                  <IntegrationGuard>
                    <FeatureGate featureSlug={SLUGS["analytics:aquisicao"]}>
                      <AnalisePedidosPage />
                    </FeatureGate>
                  </IntegrationGuard>
                }
              />
              <Route
                path="/utm"
                element={
                  <IntegrationGuard>
                    <FeatureGate featureSlug={SLUGS["analytics:utm"]}>
                      <UTMPage />
                    </FeatureGate>
                  </IntegrationGuard>
                }
              />
              <Route
                path="/pages"
                element={
                  <IntegrationGuard>
                    <FeatureGate featureSlug={SLUGS["analytics:paginas"]}>
                      <PagesPage />
                    </FeatureGate>
                  </IntegrationGuard>
                }
              />
              <Route
                path="/receita"
                element={
                  <IntegrationGuard>
                    <FeatureGate featureSlug={SLUGS["analytics:receita"]}>
                      <ReceitaPage />
                    </FeatureGate>
                  </IntegrationGuard>
                }
              />
              <Route
                path="/regiao"
                element={
                  <IntegrationGuard>
                    <FeatureGate featureSlug={SLUGS["analytics:regiao"]}>
                      <RegiaoPage />
                    </FeatureGate>
                  </IntegrationGuard>
                }
              />
              <Route
                path="/seo"
                element={
                  <IntegrationGuard>
                    <FeatureGate featureSlug={SLUGS["analytics:seo"]}>
                      <SEOPage />
                    </FeatureGate>
                  </IntegrationGuard>
                }
              />
              <Route
                path="/instagram"
                element={
                  <IntegrationGuard>
                    <FeatureGate featureSlug={SLUGS["analytics:instagram"]}>
                      <InstagramPage />
                    </FeatureGate>
                  </IntegrationGuard>
                }
              />
              <Route
                path="/historico"
                element={
                  <IntegrationGuard>
                    <FeatureGate featureSlug={SLUGS["analytics:historico"]}>
                      <HistoricoPage />
                    </FeatureGate>
                  </IntegrationGuard>
                }
              />
              <Route
                path="/ads"
                element={
                  <IntegrationGuard>
                    <FeatureGate featureSlug={SLUGS["analytics:ads"]}>
                      <ADSPage />
                    </FeatureGate>
                  </IntegrationGuard>
                }
              />
              <Route
                path="/analise-diaria"
                element={
                  <IntegrationGuard>
                    {/* A feature é a mesma que `GET /api/v1/channel-daily-analysis` exige:
                    com a outra, um plano poderia abrir a tela e receber 403 da rota. */}
                    <FeatureGate
                      featureSlug={SLUGS["analytics:analise-diaria-por-canal"]}
                    >
                      <AnaliseDiariaPage />
                    </FeatureGate>
                  </IntegrationGuard>
                }
              />
              <Route
                path="/analise-mensal"
                element={
                  <IntegrationGuard>
                    <FeatureGate
                      featureSlug={SLUGS["analytics:analise-mensal-por-canal"]}
                    >
                      <AnaliseMensalPage />
                    </FeatureGate>
                  </IntegrationGuard>
                }
              />
              {/* <auditoria 31> DETALHAMENTO ETAPA 1. A feature é a mesma que
              `GET /api/v1/sales-funnel/stage-1-breakdown` exige — e a mesma do card FUNIL
              DE VENDAS de que ela é o detalhe. Com outra, um plano poderia abrir a tela e
              receber 403 da rota. */}
              <Route
                path="/detalhamento-etapa-1"
                element={
                  <IntegrationGuard>
                    <FeatureGate featureSlug={SLUGS["analytics:visao-geral"]}>
                      <DetalhamentoEtapa1Page />
                    </FeatureGate>
                  </IntegrationGuard>
                }
              />
              <Route
                path="/analise-ticket-medio"
                element={
                  <IntegrationGuard>
                    <FeatureGate
                      featureSlug={SLUGS["analytics:analise-de-ticket-medio"]}
                    >
                      <AnaliseTicketMedioPage />
                    </FeatureGate>
                  </IntegrationGuard>
                }
              />
              <Route
                path="/melhores-resultados-meta"
                element={
                  <IntegrationGuard>
                    <FeatureGate
                      featureSlug={SLUGS["analytics:meta-melhores-resultados"]}
                    >
                      <MelhoresResultadosMetaPage />
                    </FeatureGate>
                  </IntegrationGuard>
                }
              />
            </Route>
          </Route>
          <Route path="*" element={<Navigate to="/dashboard" replace />} />
        </Routes>
      </main>

      {/* Bottom Navigation Mobile */}
      <BottomNavigationMobile onOpenDrawer={() => setDrawerOpen(true)} />

      {/* Drawer Mobile */}
      <DrawerMobile
        open={drawerOpen}
        onOpenChange={setDrawerOpen}
        onLogout={logout}
        darkMode={isDark}
        onToggleDarkMode={toggleTheme}
        user={user}
        isAgencyUser={isAgencyUser}
      />

      {/* <M-281> Feedback de problema ou melhoria, em toda tela */}
      <FeedbackButton />
    </div>
  );
};

const Dashboard = () => {
  return (
    <SidebarProvider>
      <DashboardMain />
    </SidebarProvider>
  );
};

export default Dashboard;
